import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import {
    type UpdateUserFormSchemaValues,
    updateUserFormSchema,
    useUpdateUser,
} from 'src/features/profile/api/useUpdateUser';
import { passwordMessages } from 'src/features/profile/utils';
import {
    FormFieldBlock,
    FormFieldBlockLabel,
    FormFieldBlockTitle,
    FormFieldBlockName,
    FormFieldBlockSubtitle,
    FormFieldBlockBody,
    FormFieldBlockSeparator,
} from 'src/registry/components/blocks/FormFieldBlock';
import { InputWithPassword } from 'src/registry/components/inputs/InputWithPassword';
import { useToast } from 'src/registry/hooks/UseToast';
import { Button } from 'src/registry/ui/Button';
import { Form, FormControl, FormField, FormItem, FormMessage } from 'src/registry/ui/Form';
import { generalErrorMessages, generalLabelMessages, generalMessages, validationMessages } from 'src/utils/messages';

import type { User } from 'src/features/auth';

interface ProfileInfoFormProps {
    user: User;
}

const ProfilePasswordForm = ({ user: { id } }: ProfileInfoFormProps) => {
    const { formatMessage } = useIntl();
    const { toast } = useToast();

    const form = useForm<UpdateUserFormSchemaValues>({
        resolver: zodResolver(updateUserFormSchema),
        mode: 'onChange',
        defaultValues: {
            old_password: '',
            new_password: '',
            confirm_new_password: '',
        },
    });

    const { mutate, isPending } = useUpdateUser({
        onSuccess: () => {
            form.reset({ old_password: '', new_password: '', confirm_new_password: '' });
            toast({
                title: formatMessage(passwordMessages.passwordChangeSuccess),
                description: formatMessage(passwordMessages.passwordChangeSuccessDetails),
            });
        },
        onError: (e) => {
            if (e?.response?.data) {
                const errorData = e.response.data;
                const oldPasswordErrorMessage = Array.isArray(errorData)
                    ? (errorData[0] as string)
                    : formatMessage(generalErrorMessages.somethingWentWrong);

                const isOldPasswordIncorrect: boolean = oldPasswordErrorMessage === 'Old password is incorrect';

                if (isOldPasswordIncorrect) {
                    form.setError('old_password', {
                        type: 'manual',
                        message: formatMessage(validationMessages.validationErrorOldPassword),
                    });
                }
                const toastDescription = isOldPasswordIncorrect
                    ? formatMessage(validationMessages.validationErrorOldPassword)
                    : oldPasswordErrorMessage;
                toast({
                    variant: 'destructive',
                    title: formatMessage(generalErrorMessages.somethingWentWrong),
                    description: `${formatMessage(generalLabelMessages.details)}: ${toastDescription}`,
                });
            }
        },
    });

    const onSubmit = ({ old_password, new_password }: UpdateUserFormSchemaValues) => {
        mutate({ userId: id, new_password, old_password });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(passwordMessages.oldPassword)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>
                        {formatMessage(passwordMessages.oldPasswordSubtitle)}
                    </FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="old_password"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <InputWithPassword type="password" autoComplete="current-password" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(passwordMessages.newPassword)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>
                        {formatMessage(passwordMessages.newPasswordSubtitle)}
                    </FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="new_password"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <InputWithPassword type="password" autoComplete="new-password" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <FormFieldBlock>
                <FormFieldBlockLabel>
                    <FormFieldBlockTitle>
                        <FormFieldBlockName>{formatMessage(passwordMessages.confirmNewPassword)}</FormFieldBlockName>
                    </FormFieldBlockTitle>
                    <FormFieldBlockSubtitle>
                        {formatMessage(passwordMessages.confirmNewPasswordSubtitle)}
                    </FormFieldBlockSubtitle>
                </FormFieldBlockLabel>
                <FormFieldBlockBody>
                    <FormField
                        control={form.control}
                        name="confirm_new_password"
                        render={({ field }) => (
                            <FormItem className="w-full">
                                <FormControl>
                                    <InputWithPassword type="password" autoComplete="new-password" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </FormFieldBlockBody>
            </FormFieldBlock>

            <FormFieldBlockSeparator />

            <div className="flex justify-end">
                <Button
                    className="w-full sm:w-auto"
                    type="submit"
                    disabled={!form.formState.isDirty || !form.formState.isValid || isPending}
                >
                    {isPending && <FontAwesomeIcon icon={faSpinnerThird} className="animate-spin text-lg" />}
                    {formatMessage(generalMessages.saveChanges)}
                </Button>
            </div>
        </Form>
    );
};

export default ProfilePasswordForm;
